<h3 mat-dialog-title>
    <span class="dialog-title-text">{{ dialogTitle }}</span>
    <button mat-icon-button mat-dialog-close aria-label="Close" class="dialog-close-btn">
        <i class="ph-bold ph-x icon-muted icon-20" aria-hidden="true"></i>
    </button>
</h3>

<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

<mat-dialog-content class="px-0">
    <!-- Filters Row (search lives inline at the head of the grid) -->
    <div class="history-filters">
        <form [formGroup]="filterForm" class="filter-grid">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="search-field">
                <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                <input matInput
                       type="text"
                       [formControl]="searchControl"
                       placeholder="Search…"
                       aria-label="Search history">
                <button *ngIf="searchTerm"
                        type="button"
                        matIconSuffix
                        class="search-clear-btn"
                        aria-label="Clear search"
                        (click)="clearSearch()">
                    <i class="ph-bold ph-x icon-muted icon-14" aria-hidden="true"></i>
                </button>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-select panelClass="entity-history-select-panel"
                            formControlName="eventTypes"
                            multiple
                            placeholder="All event types">
                    <mat-option *ngFor="let evt of availableEvents; trackBy: trackByEvent" [value]="evt">
                        {{ eventLabel(evt) }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput
                       [matDatepicker]="fromPicker"
                       formControlName="from"
                       placeholder="From"
                       (click)="fromPicker.open()">
                <mat-datepicker #fromPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput
                       [matDatepicker]="toPicker"
                       formControlName="to"
                       placeholder="To"
                       (click)="toPicker.open()">
                <mat-datepicker #toPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-select panelClass="entity-history-select-panel"
                            formControlName="actorId"
                            placeholder="All actors">
                    <mat-option [value]="null">All actors</mat-option>
                    <mat-option *ngFor="let actor of availableActors; trackBy: trackByActor" [value]="actor.id">
                        {{ actor.name }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
        </form>
    </div>

    <!-- Table -->
    <div class="history-table-wrapper" #historyTableWrapper>
        <table mat-table [dataSource]="visibleEntries" [trackBy]="trackById" class="history-table">
            <ng-container matColumnDef="occurredAt">
                <th mat-header-cell *matHeaderCellDef>When</th>
                <td mat-cell *matCellDef="let entry">
                    <div class="when-cell">
                        <span class="when-date">{{ formatOccurredDate(entry.occurredAt) }}</span>
                        <span class="when-time">{{ formatOccurredTime(entry.occurredAt) }}</span>
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="actor">
                <th mat-header-cell *matHeaderCellDef>Who</th>
                <td mat-cell *matCellDef="let entry">
                    <div class="actor-cell">
                        <span class="actor-name">{{ entry.actor?.name || '—' }}</span>
                        <app-simple-badge *ngIf="entry.actor?.impersonatedBy"
                                          [label]="'impersonating'"
                                          color="warning">
                        </app-simple-badge>
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="event">
                <th mat-header-cell *matHeaderCellDef>Action</th>
                <td mat-cell *matCellDef="let entry">
                    <app-status-chip [label]="eventLabelForEntry(entry)"
                                     [color]="eventColor(entry.event)">
                    </app-status-chip>
                </td>
            </ng-container>

            <ng-container matColumnDef="entity">
                <th mat-header-cell *matHeaderCellDef>What</th>
                <td mat-cell *matCellDef="let entry">
                    <span class="entity-label">{{ entityLabel(entry) }}</span>
                </td>
            </ng-container>

            <ng-container matColumnDef="details">
                <th mat-header-cell *matHeaderCellDef>Changes</th>
                <td mat-cell *matCellDef="let entry">
                    <div class="details-cell">
                        <ng-container *ngIf="entry.source === 'audit' && entry.changes?.length; else activityOrEmpty">
                            <div class="change-line"
                                 *ngFor="let change of visibleChanges(entry); trackBy: trackByField">
                                <span class="change-label">{{ change.label }}:</span>
                                <ng-container [ngSwitch]="entry.event">
                                    <ng-container *ngSwitchCase="'created'">
                                        <ng-container *ngTemplateOutlet="valueTpl; context: { value: change.newValue, label: change.newLabel, url: change.newUrl, related: change.newRelated, cls: 'change-new', field: change.field, entryId: entry.id, htmlRender: isHtmlField(entry, change.field) }"></ng-container>
                                    </ng-container>
                                    <ng-container *ngSwitchCase="'deleted'">
                                        <ng-container *ngTemplateOutlet="valueTpl; context: { value: change.oldValue, label: change.oldLabel, url: change.oldUrl, related: change.oldRelated, cls: 'change-old', field: change.field, entryId: entry.id, htmlRender: isHtmlField(entry, change.field) }"></ng-container>
                                    </ng-container>
                                    <ng-container *ngSwitchDefault>
                                        <ng-container *ngTemplateOutlet="valueTpl; context: { value: change.oldValue, label: change.oldLabel, url: change.oldUrl, related: change.oldRelated, cls: 'change-old', field: change.field, entryId: entry.id, htmlRender: isHtmlField(entry, change.field) }"></ng-container>
                                        <i class="ph-bold ph-arrow-right icon-muted icon-12" aria-hidden="true"></i>
                                        <ng-container *ngTemplateOutlet="valueTpl; context: { value: change.newValue, label: change.newLabel, url: change.newUrl, related: change.newRelated, cls: 'change-new', field: change.field, entryId: entry.id, htmlRender: isHtmlField(entry, change.field) }"></ng-container>
                                    </ng-container>
                                </ng-container>
                                <button *ngIf="isChangeLineTruncatable(change, entry.event) && !isExpanded(entry.id)"
                                        type="button"
                                        class="value-toggle-link"
                                        (click)="toggleValue(entry.id, change.field)">
                                    {{ isValueExpanded(entry.id, change.field) ? 'show less' : 'show more' }}
                                </button>
                            </div>
                            <button *ngIf="hiddenChangeCount(entry) > 0"
                                    type="button"
                                    class="btn btn-link p-0 show-more-btn"
                                    (click)="toggleChanges(entry.id)">
                                +{{ hiddenChangeCount(entry) }} more
                            </button>
                            <button *ngIf="isExpanded(entry.id) && entry.changes.length > 3"
                                    type="button"
                                    class="btn btn-link p-0 show-more-btn"
                                    (click)="toggleChanges(entry.id)">
                                Show less
                            </button>
                        </ng-container>
                        <ng-template #activityOrEmpty>
                            <span *ngIf="entry.message; else dash">{{ entry.message }}</span>
                            <ng-template #dash><span class="muted">—</span></ng-template>
                        </ng-template>
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="source">
                <th mat-header-cell *matHeaderCellDef>Source</th>
                <td mat-cell *matCellDef="let entry">
                    <app-simple-badge [label]="sourceBadgeLabel(entry.source)"
                                      [color]="entry.source === 'audit' ? 'primary' : 'muted'">
                    </app-simple-badge>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>

            <tr class="mat-row no-data-row" *matNoDataRow>
                <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
                    <ng-container *ngIf="!loading && !searchTerm">No history yet for this record.</ng-container>
                    <ng-container *ngIf="!loading && searchTerm">No rows match "{{ searchTerm }}".</ng-container>
                </td>
            </tr>
        </table>
    </div>

    <mat-paginator *ngIf="total > perPage"
                   [length]="total"
                   [pageSize]="perPage"
                   [pageSizeOptions]="[10, 25, 50, 100]"
                   [pageIndex]="pageIndex"
                   (page)="onPageChange($event)"
                   [selectConfig]="{ panelClass: 'entity-history-select-panel' }"
                   showFirstLastButtons>
    </mat-paginator>
</mat-dialog-content>

<ng-template #valueTpl
             let-value="value"
             let-label="label"
             let-url="url"
             let-related="related"
             let-cls="cls"
             let-field="field"
             let-entryId="entryId"
             let-htmlRender="htmlRender">
    <span class="value-with-drilldown">
        <!-- (1) FK-resolved with clickable URL takes precedence. -->
        <a *ngIf="url"
           [routerLink]="url"
           class="change-link"
           [ngClass]="cls"
           (click)="_dialogRef.close()">
            {{ label || formatValue(field, value) }}<span *ngIf="label" class="change-id-suffix"> ({{ value }})</span>
        </a>
        <!-- (2) Nested object / array — collapsible drill-down. -->
        <ng-container *ngIf="!url && isObjectValue(value)">
            <ng-container *ngTemplateOutlet="objectTpl; context: {
                value: value,
                cls: cls,
                path: entryId + ':' + field
            }"></ng-container>
        </ng-container>
        <!-- (3a) HTML payload — show a button that opens the rendered content
             in a nested dialog (keeps the row compact). -->
        <button *ngIf="!url && !isObjectValue(value) && htmlRender"
                type="button"
                class="value-html-button"
                [ngClass]="cls"
                (click)="openHtmlContent(value, field)">
            <i class="ph-bold ph-arrow-square-out icon-12" aria-hidden="true"></i>
            View rendered content
        </button>
        <!-- (3b) Primitive — existing inline rendering with show-more truncation. -->
        <span *ngIf="!url && !isObjectValue(value) && !htmlRender"
              class="value-text"
              [ngClass]="cls"
              [class.truncatable]="isTruncatable(field, value, label)"
              [class.expanded]="isValueExpanded(entryId, field)"
              (click)="isTruncatable(field, value, label) && toggleValue(entryId, field)">
            {{ label || formatValue(field, value) }}<span *ngIf="label" class="change-id-suffix"> ({{ value }})</span>
        </span>
        <!-- (4) Related-record drill-down: when the FK resolver attached a
             full attribute snapshot, render an inline expand toggle so users
             can drill into the related record's fields without leaving. -->
        <ng-container *ngIf="isObjectValue(related)">
            <ng-container *ngTemplateOutlet="objectTpl; context: {
                value: related,
                cls: cls,
                path: entryId + ':' + field + ':related'
            }"></ng-container>
        </ng-container>
    </span>
</ng-template>

<!--
    Recursive object renderer. Collapsed shows a one-line summary plus a
    toggle. Expanded shows a vertical key/value list, recursing through
    nested objects/arrays via valueTpl (which dispatches back here).
-->
<ng-template #objectTpl
             let-value="value"
             let-cls="cls"
             let-path="path">
    <span class="object-value" [ngClass]="cls">
        <button type="button"
                class="object-toggle"
                (click)="toggleObject(path)"
                [attr.aria-expanded]="isObjectExpanded(path)">
            <i class="ph-bold icon-12"
               [class.ph-caret-right]="!isObjectExpanded(path)"
               [class.ph-caret-down]="isObjectExpanded(path)"
               aria-hidden="true"></i>
            <span class="object-summary">{{ objectSummary(value) }}</span>
        </button>
        <ul *ngIf="isObjectExpanded(path)" class="object-entries">
            <li *ngFor="let kv of objectEntries(value); trackBy: trackByObjectEntry"
                class="object-entry">
                <span class="object-key">{{ kv.key }}:</span>
                <ng-container *ngTemplateOutlet="valueTpl; context: {
                    value: kv.value,
                    label: null,
                    url: null,
                    cls: cls,
                    field: kv.key,
                    entryId: path
                }"></ng-container>
            </li>
        </ul>
    </span>
</ng-template>

<mat-dialog-actions>
    <button type="button" class="close-btn" (click)="_dialogRef.close()">Close</button>
</mat-dialog-actions>
